/**
 * Cart Urgency Badge Component
 *
 * Displays social proof urgency messages for cart items based on GA4
 * add-to-cart counts served by the BWW middleware (`/social-proof/adds`).
 *
 * The data is NOT real-time: GA4 finalises item-scoped data on a ~24h lag, so
 * the middleware reports a trailing window ending yesterday and refreshes once
 * a day. Counts are stable within a day by design.
 *
 * Shows "ACT FAST! X others have this in their cart" on the HIGHEST PRICED
 * cart item when its add-to-cart count exceeds the threshold.
 *
 * Uses BWW state.watch('cartState') to react to cart changes and
 * fires on every cart update event.
 */

// @ts-nocheck
(() => {
  // The middleware holds the GA4 credentials; the storefront only knows this URL.
  const middlewareBase = () => String(window.BWWMiddleware?.baseUrl || "").replace(/\/+$/, "");
  // Trailing window, in days, for the add-to-cart count.
  const DAYS = 2;
  const MIN_ITEMS_THRESHOLD = 5;
  const MAX_DISPLAY_COUNT = 200;

  // Console logging. DEBUG is the default, but logging can also be switched on at
  // runtime so the middleware payload can be inspected on any live page without a
  // theme push — for QA, or to debug one shopper's session:
  //   ?spdebug=1 in the URL, or
  //   localStorage.setItem('bww:debug:social-proof', '1')  (persists; remove to stop)
  const DEBUG = false;
  const DEBUG_KEY = "bww:debug:social-proof";
  const isDebug = () => {
    try {
      if (window.__bwwSocialProofDebug != null) return Boolean(window.__bwwSocialProofDebug);
      if (localStorage.getItem(DEBUG_KEY)) return true;
      if (new URLSearchParams(window.location.search).has("spdebug")) return true;
    } catch {
      // Private mode / blocked storage — fall through to the compiled default
    }
    return DEBUG;
  };
  const log = (...args) => isDebug() && console.log("[CartUrgencyBadge]", ...args);

  /**
   * One structured line describing what the middleware returned for this SKU.
   *
   * `matched: false` is the single most useful thing here: it means GA4 has no
   * row for this SKU, which is the expected state for a new or low-traffic
   * product — and is also what it looks like if Shopify SKUs do not line up with
   * GA4's itemId for a product type. Same hidden badge either way.
   *
   * @param {string} url
   * @param {string} sku
   * @param {any} data parsed response body
   * @param {number} count parsed add-to-cart count
   */
  const logResponse = (url, sku, data, count) => {
    if (!isDebug()) return;
    console.info("[CartUrgencyBadge] middleware response", {
      url,
      sku,
      matched: Array.isArray(data?.value) && data.value.length > 0,
      itemsAddedToCart: count,
      threshold: MIN_ITEMS_THRESHOLD,
      willShow: count > MIN_ITEMS_THRESHOLD,
      asOf: data?.asOf ?? null,
      window: data?.window ?? null,
      stale: data?.stale ?? null,
      unavailable: data?.unavailable ?? false,
      raw: data,
    });
  };

  /**
   * Cart Urgency Badge Custom Element
   * Renders an urgency message for a cart item
   */
  class CartUrgencyBadge extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
    }

    /**
     * Updates the badge with the count of others who have this item
     * @param {number|string} count - Number of others with this item in cart
     */
    updateCount(count) {
      const countEl = this.querySelector(".cart-urgency-badge__count");
      if (countEl) {
        const displayCount = count > MAX_DISPLAY_COUNT ? `${MAX_DISPLAY_COUNT}+` : count;
        countEl.textContent = displayCount;
        log("Updated count element to:", displayCount, "| Element:", countEl);
      } else {
        log("⚠️ Count element not found in badge!");
      }
    }

    /**
     * Shows the urgency badge
     */
    show() {
      this.classList.remove("hidden");
      // Force display style in case Tailwind class isn't being removed
      this.style.display = "flex";
      log("Badge show() called | classList:", this.className, "| display:", this.style.display);
    }

    /**
     * Hides the urgency badge
     */
    hide() {
      this.classList.add("hidden");
      this.style.display = "";
    }
  }

  // Register the custom element
  if (!customElements.get("cart-urgency-badge")) {
    customElements.define("cart-urgency-badge", CartUrgencyBadge);
  }

  /**
   * Cart Urgency Manager (Singleton)
   *
   * Uses the same pattern as component-gwp-progressbar.js:
   * - BWW.ready() for initial cart state
   * - state.watch('cartState') for cart changes
   * - MutationObserver to re-apply badge after morphSection
   */
  class CartUrgencyManager {
    constructor() {
      this._isProcessing = false;
      this._apiCache = new Map(); // Cache API results by productId
      this._lastProcessTime = 0;
      this._pendingProcess = null;
      this._delayedChecks = []; // Store delayed check timeouts
      this._observer = null;
      this._lastBadgeState = null; // Store last shown badge state {sku, count}
    }

    /**
     * Initialize the manager
     */
    init() {
      // Initial processing
      this._scheduleProcess("init");

      // Hook into BWW state manager (like GWP progressbar does)
      if (typeof BWW !== "undefined") {
        BWW.ready(state => {
          const cart = state.state?.cartState || null;
          if (cart) {
            this._scheduleProcess("BWW.ready");
          }

          // Watch for cart state changes
          // Cart state is updated BEFORE morphSection runs, so we need to wait
          state.watch(
            "cartState",
            cart => {
              if (cart) {
                // Wait for morphSection to complete (morph happens after state update)
                // Use a longer delay to ensure DOM is fully updated
                this._scheduleProcess("cartState.watch");
              }
            },
            { immediate: false }
          );
        });
      }

      // Also listen to cart:update event directly for additional coverage
      document.addEventListener("cart:update", () => {
        log("📢 cart:update event fired");
        this._scheduleProcess("cart:update");
      });

      // Start observing cart drawer for DOM changes (morphSection resets)
      this._startCartObserver();
    }

    /**
     * Start MutationObserver to watch for cart DOM changes
     * Re-applies badge when morphSection resets the HTML
     */
    _startCartObserver() {
      // Find cart drawer container
      const cartDrawer = document.querySelector("cart-drawer") || document.querySelector("[data-cart-drawer]");
      if (!cartDrawer) {
        log("Cart drawer not found, will retry...");
        // Retry after a delay if cart drawer not found yet
        setTimeout(() => this._startCartObserver(), 1000);
        return;
      }

      if (this._observer) {
        this._observer.disconnect();
      }

      this._observer = new MutationObserver(mutations => {
        // Check if any badge was reset (has hidden class added back)
        let shouldReapply = false;

        for (const mutation of mutations) {
          // Check for childList changes (DOM replacement by morphSection)
          if (mutation.type === "childList") {
            // Check if badge elements were added (morphSection replaced them)
            const addedBadges = [...mutation.addedNodes].filter(
              node =>
                node.nodeType === 1 &&
                (node.matches?.("cart-urgency-badge") || node.querySelector?.("cart-urgency-badge"))
            );
            if (addedBadges.length > 0) {
              shouldReapply = true;
              break;
            }
          }

          // Check for attribute changes on badge elements
          if (mutation.type === "attributes" && mutation.target.tagName === "CART-URGENCY-BADGE") {
            if (mutation.attributeName === "class" && mutation.target.classList.contains("hidden")) {
              shouldReapply = true;
              break;
            }
          }
        }

        if (shouldReapply && this._lastBadgeState) {
          log("🔄 DOM changed, re-applying badge state...");
          // Small delay to ensure DOM is fully updated
          setTimeout(() => this._reapplyBadgeState(), 50);
        }
      });

      this._observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      log("Cart observer started on:", cartDrawer.tagName);
    }

    /**
     * Re-apply the last known badge state after DOM reset
     * If the original SKU badge is not found, re-process to find the new highest priced item
     */
    _reapplyBadgeState() {
      if (!this._lastBadgeState) {
        log("🔄 No last badge state to re-apply");
        return;
      }

      const { sku, count } = this._lastBadgeState;

      // Find all badges
      const badges = document.querySelectorAll("cart-urgency-badge[data-sku]");
      log("🔄 Re-applying - Found", badges.length, "badges, looking for SKU:", sku);

      if (badges.length === 0) {
        log("🔄 No badges found in DOM");
        return;
      }

      // Check if the original SKU badge still exists
      let foundOriginalSku = false;
      let applied = false;

      badges.forEach(badge => {
        if (badge.dataset.sku === sku) {
          foundOriginalSku = true;
          if (count > MIN_ITEMS_THRESHOLD) {
            log("🔄 Re-applying badge to element:", badge, "| SKU:", sku, "| Count:", count);
            badge.updateCount(count);
            badge.show();
            applied = true;
          }
        } else {
          badge.hide();
        }
      });

      // If original SKU badge wasn't found, the cart items changed (GWP added/removed)
      // Re-process to find the new highest priced item
      if (!foundOriginalSku) {
        log("⚠️ Original SKU badge not found, cart items changed. Re-processing...");
        this._isProcessing = false; // Reset to allow processing
        this._processCartItems("reapply-reprocess");
      } else if (!applied) {
        log("🔄 Badge found but not applied (count may be below threshold)");
      }
    }

    /**
     * Schedule a process with debouncing
     * Waits for DOM to stabilize after cart state changes
     * Runs multiple delayed checks to handle GWP product additions/removals
     * @param {string} source - What triggered the process
     */
    _scheduleProcess(source) {
      // Clear any pending processes
      if (this._pendingProcess) {
        clearTimeout(this._pendingProcess);
      }

      // Clear any delayed checks
      this._delayedChecks.forEach(timeout => clearTimeout(timeout));
      this._delayedChecks = [];

      log("📅 Scheduling process from:", source);

      // Run multiple checks at different intervals to handle GWP and morphSection timing
      // Check 1: After 300ms (initial morphSection)
      this._pendingProcess = setTimeout(() => {
        this._pendingProcess = null;
        this._processCartItems(source + " [300ms]");
      }, 300);

      // Check 2: After 1 second (handles GWP product addition/removal)
      this._delayedChecks.push(
        setTimeout(() => {
          log("⏰ Running delayed check (1s)...");
          this._reapplyBadgeState();
        }, 1000)
      );

      // Check 3: After 2 seconds (final safety check)
      this._delayedChecks.push(
        setTimeout(() => {
          log("⏰ Running delayed check (2s)...");
          this._reapplyBadgeState();
        }, 2000)
      );
    }

    /**
     * Process cart items and show urgency badge on the highest priced item
     * @param {string} source - What triggered this processing
     */
    async _processCartItems(source) {
      // Prevent concurrent processing
      if (this._isProcessing) {
        return;
      }

      this._isProcessing = true;
      this._lastProcessTime = Date.now();

      log("Processing triggered by:", source);

      try {
        // Find all cart items with urgency badges (fresh query after morph)
        const badges = document.querySelectorAll("cart-urgency-badge[data-sku]");
        log("Found badges:", badges.length);

        if (badges.length === 0) {
          // Hide any stale badges
          document.querySelectorAll("cart-urgency-badge").forEach(badge => badge.hide());
          return;
        }

        // Find the highest priced item
        let highestPriceItem = null;
        let highestPrice = 0;

        badges.forEach(badge => {
          const price = parseInt(badge.dataset.itemPrice, 10) || 0;
          if (price > highestPrice) {
            highestPrice = price;
            highestPriceItem = badge;
          }
        });

        if (!highestPriceItem) {
          log("No highest price item found");
          badges.forEach(badge => badge.hide());
          return;
        }

        const sku = highestPriceItem.dataset.sku;
        log("Highest priced item:", { sku, title: highestPriceItem.dataset.productTitle, price: highestPrice });

        if (!sku) {
          return;
        }

        // Fetch social proof data for the highest priced item
        const count = await this._fetchSocialProofData(sku);
        log("API count for SKU", sku, ":", count, "| Threshold:", MIN_ITEMS_THRESHOLD);

        // Re-query badges in case DOM changed during API call
        const currentBadges = document.querySelectorAll("cart-urgency-badge[data-sku]");

        // Update the UI - show badge only on the highest priced item if count meets threshold
        if (count > MIN_ITEMS_THRESHOLD) {
          // Store the badge state for re-application after morphSection
          this._lastBadgeState = { sku, count };
          log("✅ Showing badge with count:", count);

          currentBadges.forEach(badge => {
            if (badge.dataset.sku === sku) {
              badge.updateCount(count);
              badge.show();
            } else {
              badge.hide();
            }
          });
        } else {
          // Clear badge state if count is below threshold
          this._lastBadgeState = null;
          log("❌ Count below threshold:", count, "<=", MIN_ITEMS_THRESHOLD);
          currentBadges.forEach(badge => badge.hide());
        }
      } catch (error) {
        console.error("[CartUrgencyBadge] Error processing cart items:", error);
      } finally {
        this._isProcessing = false;
      }
    }

    /**
     * Fetch social proof data from the analytics API (with caching)
     * @param {string} sku - Product SKU
     * @returns {Promise<number>} - Number of items added to cart
     */
    async _fetchSocialProofData(sku) {
      try {
        // Check cache first
        if (this._apiCache.has(sku)) {
          const cached = this._apiCache.get(sku);
          log("Using cached data for SKU:", sku, "| Count:", cached);
          return cached;
        }

        const base = middlewareBase();
        if (!base) {
          console.warn(
            "[CartUrgencyBadge] window.BWWMiddleware.baseUrl is unset — check the Middleware Base URL theme setting"
          );
          return 0;
        }

        const url = `${base}/social-proof/adds?days=${DAYS}&productIds=${encodeURIComponent(sku)}`;
        log("Fetching API:", url);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const data = await response.json();

        // No row for this SKU (new or low-traffic product) => empty list => 0,
        // which keeps the badge hidden rather than rendering a blank count.
        const itemsAddedToCart = parseInt(data.value?.[0]?.itemsAddedToCart, 10) || 0;
        logResponse(url, sku, data, itemsAddedToCart);

        // Cache the result for 5 minutes
        this._apiCache.set(sku, itemsAddedToCart);
        setTimeout(() => this._apiCache.delete(sku), 5 * 60 * 1000);

        return itemsAddedToCart;
      } catch (error) {
        console.error("[CartUrgencyBadge] Error fetching social proof data:", error);
        return 0;
      }
    }
  }

  // Singleton instance
  let managerInstance = null;

  /**
   * Initialize the urgency badge manager
   */
  function boot() {
    // Only create one manager instance
    if (managerInstance) {
      managerInstance._scheduleProcess("reboot");
      return;
    }

    managerInstance = new CartUrgencyManager();
    managerInstance.init();
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-initialize on Shopify section loads (theme editor)
  document.addEventListener("shopify:section:load", boot);

  /**
   * QA helper: every cart line, which one is eligible for the badge, and the
   * count behind it. Only the highest-priced line can show the badge, so a line
   * with a big count and no badge is usually correct, not a bug — the table
   * makes that visible rather than leaving it to be guessed.
   *
   *   __CartUrgencyBadge__.qa()
   */
  function qa() {
    const badges = [...document.querySelectorAll("cart-urgency-badge[data-sku]")];
    if (badges.length === 0) {
      console.warn("[CartUrgencyBadge] no cart badges in the DOM — open the cart drawer first");
      return [];
    }
    const highest = badges.reduce(
      (best, b) => ((parseInt(b.dataset.itemPrice, 10) || 0) > (parseInt(best?.dataset.itemPrice, 10) || 0) ? b : best),
      badges[0]
    );
    const rows = badges.map(badge => ({
      product: badge.dataset.productTitle,
      sku: badge.dataset.sku || "(no SKU — cannot be matched)",
      linePrice: badge.dataset.itemPrice,
      eligible: badge === highest,
      visible: !badge.classList.contains("hidden"),
      count: badge.querySelector(".cart-urgency-badge__count")?.textContent ?? "—",
    }));
    console.table(rows);
    console.info(
      `[CartUrgencyBadge] threshold=${MIN_ITEMS_THRESHOLD} days=${DAYS} cap=${MAX_DISPLAY_COUNT} | ` +
        "only the highest-priced line is eligible | set localStorage 'bww:debug:social-proof' to see the API payload"
    );
    return rows;
  }

  // Expose for debugging
  window.__CartUrgencyBadge__ = {
    CartUrgencyBadge,
    CartUrgencyManager,
    boot,
    qa,
    getInstance: () => managerInstance,
    reprocess: () => managerInstance?._scheduleProcess("manual"),
  };
})();
